import { Link } from 'react-router-dom';

// Heart SVG Component
const HeartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

// Cross SVG Component
const CrossIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 4v16m-4-8h8" />
  </svg>
);

// Sun/Light SVG Component
const LightIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

export function About() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-white text-[#0f172a]" data-testid="about-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="h-px w-16 bg-gold-400" />
              <HeartIcon className="w-6 h-6 text-gold-500" />
              <div className="h-px w-16 bg-gold-400" />
            </div>
            <h1 className="font-cinzel text-4xl sm:text-5xl font-bold text-emerald-800 mb-4">
              The Grace Within
            </h1>
            <p className="font-cinzel text-lg text-ocean-600 tracking-widest uppercase">
              Glaucia Veronica Juarez de Leon
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Artist Portrait - Véri */}
            <div className="flex justify-center order-2 lg:order-1">
              <div className="relative w-80 h-80 flex items-center justify-center">
                <img 
                  src="/veri-portrait.png" 
                  alt="Glaucia Veronica Juarez de Leon" 
                  className="w-full h-full object-cover rounded-full shadow-2xl border-4 border-gold-400"
                />
              </div>
            </div>

            {/* Bio Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <p className="font-lora text-lg text-gray-700 leading-relaxed">
                Welcome to <strong className="text-emerald-700">Couleurs de Joie</strong>. I am Glaucia Veronica, 
                and my greatest calling is to serve as a conduit for His eternal light. Through colors, forms, 
                and silent prayers woven into every creation, I seek to reflect the profound beauty of our faith.
              </p>
              
              <p className="font-lora text-lg text-gray-700 leading-relaxed">
                Every stroke of the brush, every hue of emerald green and ocean blue, is inspired by the vastness 
                of God's creation and the depth of His mercy. It is my sincere hope that the art you find here 
                resonates within your soul, bringing a sense of peace, healing, and overflowing joy.
              </p>
              
              <blockquote className="font-lora text-emerald-700 italic border-l-4 border-gold-400 pl-6 py-3 bg-emerald-50 rounded-r">
                "Let your light shine before others, that they may see your good deeds and glorify your Father in heaven."
                <cite className="block mt-2 text-sm text-gray-500 not-italic">— Matthew 5:16</cite>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-emerald-900 text-white border-t-8 border-gold-500" data-testid="about-mission">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <CrossIcon className="w-12 h-12 text-gold-400 mx-auto mb-4" />
            <h2 className="font-cinzel text-3xl font-bold text-gold-400 mb-4 glowing-text">
              A Sacred Mission
            </h2>
            <p className="font-lora text-emerald-100">
              To transmit love, joy, and light through the universal language of art
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {/* Love */}
            <div className="text-center p-8 bg-ocean-900/50 rounded-sm border border-gold-600/20">
              <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="font-cinzel text-xl text-gold-100 mb-3">Love</h3>
              <p className="font-lora text-sm text-emerald-100/70 leading-relaxed">
                Each creation is born from a heart overflowing with divine love, 
                meant to touch and uplift every soul that encounters it.
              </p>
            </div>

            {/* Joy */}
            <div className="text-center p-8 bg-ocean-900/50 rounded-sm border border-gold-600/20">
              <div className="w-16 h-16 bg-amber-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </div>
              <h3 className="font-cinzel text-xl text-gold-100 mb-3">Joy</h3>
              <p className="font-lora text-sm text-emerald-100/70 leading-relaxed">
                Colors dance with celestial delight, bringing moments of pure joy 
                and spiritual awakening to all who behold them.
              </p>
            </div>

            {/* Light */}
            <div className="text-center p-8 bg-ocean-900/50 rounded-sm border border-gold-600/20">
              <div className="w-16 h-16 bg-sky-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <LightIcon className="w-8 h-8 text-sky-400" />
              </div>
              <h3 className="font-cinzel text-xl text-gold-100 mb-3">Light</h3>
              <p className="font-lora text-sm text-emerald-100/70 leading-relaxed">
                Illuminating the path of faith, each piece reflects the eternal light 
                that guides us through darkness into grace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-ocean-900 text-white border-t-8 border-ocean-800" data-testid="about-cta">
        <div className="container-main text-center">
          <h2 className="font-cinzel text-3xl font-bold text-gold-400 mb-6 glowing-text">
            Experience the Divine
          </h2>
          <p className="font-lora text-ocean-200 max-w-2xl mx-auto mb-8">
            Step into The Sanctuary and discover art that speaks to the soul. 
            Each piece awaits to share its story of faith, hope, and eternal love.
          </p>
          <Link 
            to="/sanctuary" 
            className="btn-gold inline-block"
            data-testid="about-cta-button"
          >
            Enter the Sanctuary
          </Link>
        </div>
      </section>
    </div>
  );
}
